<template>
  <VRow ref="root" class="add-processing" no-gutters>
    <SLoadingOverlay :loading="loading" />
    <VCol
      v-if="demonstrators.length === 0"
      data-testid="demonstratorsVFlex"
      class="px-4 pt-2 pb-6 border-top"
    >
      <ObservedForm
        ref="observer"
        noerror
        @submit="searchByLocation"
      >
        <VRow class="mt-0 pb-5">
          <VCol>
            <div class="d-flex">
              <VIcon
                class="pr-1"
                color="primary"
              >
                {{ icons.mdiMapMarkerOutline }}
              </VIcon>
              <SBtn
                :disabled="loading"
                link-medium
                data-testid="searchByProximityButton"
                class="text-uppercase font-weight-medium"
                @click="searchByProximity"
              >
                <span>{{ $t('demonstrator.label.nearme') }}</span>
              </SBtn>
            </div>
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol>
            <TextFieldWithValidation
              v-model="searchLocation"
              :aria-label="$t('demonstrator.label.addressOrZip')"
              :placeholder="$t('demonstrator.label.addressOrZip')"
              :rules="findLocationRules"
              name="location"
              data-testid="demo-search-near-field"
            />
            <SBtn
              :disabled="loading"
              class="d-flex ml-auto"
              color="secondary"
              type="submit"
              data-testid="demo-search-near-button"
              link-medium
            >
              {{ $t('input.btn.search') }}
            </SBtn>
            <p
              v-if="noResults"
              ref="noMatches"
              class="mt-3 d-block text-center"
              tabindex="-1"
              data-testid="demo-no-results"
            >
              {{ $t('demonstrator.label.noMatchesAddress') }}
            </p>
          </VCol>
        </VRow>
      </ObservedForm>
    </VCol>
    <VCol v-if="demonstrators.length > 0">
      <DemonstratorListComponent
        :demonstrators="demonstrators"
        @demo-selected="emits('demo-selected', $event)"
      />
      <VDivider class="my-0" />
      <VList
        data-testid="demonstratorsList"
        class="pa-0 text-left"
      >
        <VListItem class="px-4 py-0">
          <SBtn
            v-if="showMoreButton"
            :disabled="loading"
            data-testid="showMoreButton"
            class="text-uppercase font-weight-medium"
            color="secondary"
            link-medium
            @click="showMore"
          >
            {{ $t('input.btn.showMore') }}
          </SBtn>
          <template #append>
            <button
              type="button"
              class="icon-btn mr-n1"
              data-testid="reset-demo-location-search"
              :aria-label="$t('input.btn.newSearch')"
              @click="newSearch"
            >
              <SIcon :icon="icons.mdiAccountSearch" color="primary" size="24" decorative />
            </button>
          </template>
        </VListItem>
      </VList>
    </VCol>
  </VRow>
</template>

<script lang="ts" setup>
import { mdiMapMarkerOutline, mdiAccountSearch } from '@mdi/js'
import type { Demonstrator } from '~/types/account'
import { getCurrentGeoPosition } from '~/utils/general'
import DemonstratorListComponent from '~/components/account/demonstrator/DemonstratorList.vue'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import { RegionAndCountryFields } from '~/types/locale'
import { useLocaleStore } from '~/stores/locale'
import { firstDemonstratorResult, focusSearchOutcome } from '~/utils/focus'
import {useTemplateRef} from "vue";

const icons = { mdiMapMarkerOutline, mdiAccountSearch }
const localeStore = useLocaleStore()
const { loading, processAction } = useAction()

const props = withDefaults(defineProps<{
  postalCode?: string
  proximityEndpoint?: string
  locationEndpoint?: string
}>(), {
  postalCode: '',
  proximityEndpoint: 'demonstrator/proximity',
  locationEndpoint: 'demonstrator/location'
})

const emits = defineEmits<{
  (e: 'demo-selected', demonstrator: Demonstrator): void
}>()

const findLocationRules = computed(() => localeStore.localeData(RegionAndCountryFields.demoFinderRules) ?? undefined)

const observer = useTemplateRef('observer')
const root = useTemplateRef('root')
const noMatches = useTemplateRef('noMatches')

// Land on the first result or the no-matches message, as nuxt2 does (PR 23563). AB#65420
const focusOutcome = () =>
  focusSearchOutcome(
    () => root.value?.$el as HTMLElement | undefined,
    finder => (demonstrators.value.length > 0 ? firstDemonstratorResult(finder) : noMatches.value)
  )

const demonstrators = ref<Demonstrator[]>([])
const searchByLatLong = ref(false)
const searchLocation = ref<string>('')
const showMoreButton = ref(true)
const page = ref(0)
const pageMax = 20
const noResults = ref(false)
const latitude = ref(0)
const longitude = ref(0)

const searchByLocation = async () => {
  await processAction(async () => {
    if (searchLocation.value) {
      noResults.value = false
      searchByLatLong.value = false
      const { totalPages, results } = await $api()<{ totalPages: number, results: Demonstrator[] }>(props.locationEndpoint, { query: { address: searchLocation.value, page: 0 } })
      demonstrators.value = results as Demonstrator[]
      showMoreButton.value = totalPages > 1
      noResults.value = totalPages === 0
      if (noResults.value) {
        searchLocation.value = ''
        observer.value?.resetForm()
      }
    }
  })
  await focusOutcome()
}

const searchByLocationMore = async () => {
  await processAction(async () => {
    const { totalPages, results } = await $api()<{ totalPages: number, results: Demonstrator[] }>(props.locationEndpoint, { query: { address: searchLocation.value, page: page.value } })
    demonstrators.value.push(...results)
    showMoreButton.value = totalPages > (page.value + 1) && page.value < pageMax
  })
}

const searchByProximity = async () => {
  await processAction(async () => {
    noResults.value = false
    searchByLatLong.value = true
    const position = await getCurrentGeoPosition()
    latitude.value = position.coords.latitude
    longitude.value = position.coords.longitude
    const { totalPages, results } = await $api()<{ totalPages: number, results: Demonstrator[] }>(props.proximityEndpoint, { query: { latitude: latitude.value, longitude: longitude.value, page: 0 } })
    demonstrators.value = results as Demonstrator[]
    showMoreButton.value = totalPages > 1
    noResults.value = totalPages === 0
  })
  await focusOutcome()
}

const searchByProximityMore = async () => {
  await processAction(async () => {
    const { totalPages, results } = await $api()<{ totalPages: number, results: Demonstrator[] }>(props.proximityEndpoint, { query: { latitude: latitude.value, longitude: longitude.value, page: page.value } })
    demonstrators.value.push(...results)
    showMoreButton.value = totalPages > (page.value + 1) && page.value < pageMax
  })
}

onMounted(() => {
  searchLocation.value = props.postalCode
})

const newSearch = async () => {
  page.value = 0
  searchLocation.value = ''
  demonstrators.value = []
  // The reset button unmounts with the results; hand focus to the search input.
  await nextTick()
  const field = document.querySelector<HTMLElement>('[data-testid="demo-search-near-field"]')
  const input = field instanceof HTMLInputElement ? field : field?.querySelector('input')
  input?.focus()
}

const showMore = async () => {
  if (page.value < pageMax) {
    page.value++
    if (searchByLatLong.value) {
      await searchByProximityMore()
    } else {
      await searchByLocationMore()
    }
    document.querySelector('.demo-list .v-list-item:last-child')?.scrollIntoView()
  }
}
</script>

<style lang="scss" scoped>
.border-top {
  border-top: 1px solid rgb(var(--v-theme-border));
}
.background-grey {
  background-color: rgb(var(--v-theme-backgroundDiv));
}
</style>
